import * as React from 'react'

import { Form } from 'factorial-form'
import groupBy from 'lodash/groupBy'
import uniq from 'lodash/uniq'
import xor from 'lodash/xor'

import { observer } from 'mobx-react'

import useAsyncAction from 'modules/core/hooks/useAsyncAction'
import { useAsyncData } from 'modules/core/hooks/useAsyncData'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { apiClient as ApiClient } from 'modules/core/lib/mobx-rest'
import users from 'modules/core/store/collections/users'
import type User from 'modules/core/store/models/User'

import ninegridboxes from 'modules/competencies/store/collections/ninegridboxes'
import ninegridboxessetup from 'modules/competencies/store/collections/ninegridboxessetup'
import type NineGridBox from 'modules/competencies/store/models/NineGridBox'

interface Values {
  loading: boolean
  loadingSetup: boolean
  onSelectCompetency: (competencyId: number, isXaxis: boolean) => void
  isChecked: (id: number) => boolean
  filterChildren: (competencyId: number, isXaxis: boolean) => number[]
  selectableCompetencyIds: number[]
  getUserCompetencies: (userIds: number[]) => Promise<void>
  form: NineboxForm
  data: Array<{
    accessId: number
    xAxisValues: number[]
    yAxisValues: number[]
    xAxis: number[]
    yAxis: number[]
  }>
  loadingUsers: boolean
  filteredUsers: User[]
}

export class NineboxForm extends Form {
  constructor(values: {}) {
    super(values, {
      id: 'number',
      owner_id: 'number',
      access_ids: 'any',
      x_axis_name: 'string',
      y_axis_name: 'string',
      x_axis_ids: 'any',
      y_axis_ids: 'any',
    })
  }
}

const Context = React.createContext<Values | null>(null)

export const NineGridBoxProvider = observer(({ children }: React.PropsWithChildren<{}>) => {
  const [reporteesIds, setReporteeIds] = React.useState([])
  const {
    access: { id: ownerId },
  } = useCurrentSession()
  const formRef = React.useRef<NineboxForm>(new NineboxForm({}))
  const form = formRef.current
  const api = React.useRef(ApiClient()).current

  const { loading: loadingUsers } = useAsyncData(async () => {
    const response = await api.get('/competencies/evaluated_employees')
    setReporteeIds(response)
  }, [])

  const filteredUsers = reporteesIds.map((id) => users.get(id)).filter(Boolean) as User[]

  const { loading: loadingSetup } = useAsyncData(async () => {
    await ninegridboxessetup.fetch({ data: { owner_id: ownerId } })

    if (!ninegridboxessetup.isEmpty) {
      const setupValues = Object.assign({}, ...ninegridboxessetup.models)

      if (setupValues.initialAttributes.access_ids.length === 0) return

      await ninegridboxes.customIndex({
        data: { access_ids: setupValues.initialAttributes.access_ids },
      })

      formRef.current = new NineboxForm({
        id: setupValues.initialAttributes.id,
        owner_id: setupValues.initialAttributes.owner_id,
        access_ids: setupValues.initialAttributes.access_ids,
        x_axis_name: setupValues.initialAttributes.x_axis_name,
        y_axis_name: setupValues.initialAttributes.y_axis_name,
        x_axis_ids: setupValues.initialAttributes.x_axis_ids,
        y_axis_ids: setupValues.initialAttributes.y_axis_ids,
      })
    }
  }, [])

  const selectedCompetenciesIds = [...form.get('x_axis_ids').value, ...form.get('y_axis_ids').value]

  const isChecked = (id: number) => {
    return selectedCompetenciesIds.includes(id)
  }

  const onSelectCompetency = (competencyId: number, isXaxis: boolean) => {
    if (isXaxis) {
      form.get('x_axis_ids').set(xor(form.get('x_axis_ids').value, [competencyId]))
    } else {
      form.get('y_axis_ids').set(xor(form.get('y_axis_ids').value, [competencyId]))
    }
  }

  const filterChildren = (competencyId: number, isXaxis: boolean) => {
    const competenciesIds = isXaxis ? form.get('y_axis_ids').value : form.get('x_axis_ids').value
    const ninegridbox = ninegridboxes.getCompetency(competencyId)

    return (
      ninegridbox?.children?.filter((competencyId) => !competenciesIds?.includes(competencyId)) ||
      []
    )
  }

  const allCompetencyIds = uniq(ninegridboxes.map((n) => n.competencyId))
  const selectableCompetencyIds: number[] = xor(allCompetencyIds, selectedCompetenciesIds)

  const { running: loading, performAction: getUserCompetencies } = useAsyncAction(
    async (accessIds: number[]): Promise<void> => {
      if (accessIds.length > 0) {
        ninegridboxes.customIndex({
          data: { access_ids: accessIds },
        })
      } else {
        ninegridboxes.reset([])
      }

      form.setValues({
        access_ids: accessIds,
      })
    },
    { successFlash: false }
  )

  const xCompetencies = new Set(form.get('x_axis_ids').value)
  const yCompetencies = new Set(form.get('y_axis_ids').value)
  const dataByUser: { [accessId: string]: NineGridBox[] } = groupBy(
    ninegridboxes.toArray(),
    (ninegridbox: NineGridBox) => ninegridbox.accessId
  )

  const data: Array<{
    accessId: number
    xAxisValues: number[]
    yAxisValues: number[]
    xAxis: number[]
    yAxis: number[]
  }> = []
  Object.keys(dataByUser).forEach((accessId) => {
    const ninegridboxForAccess = dataByUser[accessId]

    if (!ninegridboxForAccess) return

    const xAxisValues: number[] = []
    const yAxisValues: number[] = []
    const xAxis: number[] = []
    const yAxis: number[] = []
    ninegridboxForAccess.forEach((ninegridbox: NineGridBox) => {
      if (xCompetencies.has(ninegridbox.competencyId)) {
        xAxisValues.push(ninegridbox.nineGridValue)
        xAxis.push(ninegridbox.value)
      }

      if (yCompetencies.has(ninegridbox.competencyId)) {
        yAxisValues.push(ninegridbox.nineGridValue)
        yAxis.push(ninegridbox.value)
      }
    })

    data.push({
      accessId: Number(accessId),
      xAxisValues,
      yAxisValues,
      xAxis,
      yAxis,
    })
  })

  const values: Values = {
    loading,
    loadingSetup,
    onSelectCompetency,
    isChecked,
    filterChildren,
    selectableCompetencyIds,
    getUserCompetencies: (userIds) => getUserCompetencies(userIds).then(() => {}),
    form,
    data,
    loadingUsers,
    filteredUsers,
  }

  return <Context.Provider value={values}>{children}</Context.Provider>
})

export const useNineGridBox = (): Values => {
  const values = React.useContext(Context)

  if (!values) throw new Error('useNineGridBox must be used within a NineGridBoxProvider')

  return values
}
